import { ref, reactive, onMounted, onUnmounted } from "vue"
import { Billing } from "./index"
import type {
	PurchaseInfo,
	SubscriptionDetails,
	BillingError,
	ConnectionResult,
	PluginListenerHandle,
} from "./definitions"

import { removeBanner } from "@/utils/admob"
import { useUserStore } from "@/stores/user"

/**
 * Vue composable for using the Billing plugin
 * @returns Reactive billing state and methods
 */
export function useBilling(options: { loadSubscriptionsOnReady?: boolean } = {}) {
	const { loadSubscriptionsOnReady = true } = options
	const isReady = ref(false)
	const isConnecting = ref(false)
	const connectionError = ref<string | null>(null)

	const userStore = useUserStore()

	// Subscription data
	const subscriptions = reactive<Record<string, PurchaseInfo>>({})
	const pendingPurchases = reactive<Record<string, PurchaseInfo>>({})
	const subscriptionDetails = reactive<Record<string, SubscriptionDetails>>({})

	// Last error
	const lastError = ref<BillingError | Error | null>(null)

	// Store listener handles for cleanup
	const listenerHandles: PluginListenerHandle[] = []

	// Setup event listeners
	const setupListeners = async () => {
		try {
			// Connection status
			const billingReadyListener = await Billing.addListener(
				"billingReady",
				(info: ConnectionResult) => {
					// alert("IN billingReady Listener")
					isConnecting.value = false
					isReady.value = info.connected
					if (!info.connected) {
						connectionError.value = info.error || null
					} else {
						connectionError.value = null
					}
				}
			)
			listenerHandles.push(billingReadyListener)
			// Purchase updates
			const purchaseUpdatedListener = await Billing.addListener(
				"purchaseUpdated",
				(purchase: PurchaseInfo) => {
					// alert("IN purchaseUpdated Listener")
					if (purchase.sku) {
						subscriptions[purchase.sku] = purchase
						if (purchase.purchaseState === 1 && purchase.sku.includes("plus")) {
							userStore.user.subscription_type = "plus"
						}
					}
				}
			)
			listenerHandles.push(purchaseUpdatedListener)
			// Purchase errors
			const purchaseErrorListener = await Billing.addListener(
				"purchaseError",
				(error: BillingError) => {
					// alert("IN purchaseError Listener")
					lastError.value = error
				}
			)
			listenerHandles.push(purchaseErrorListener)
			// Purchase pending state
			const purchasePendingListener = await Billing.addListener(
				"purchasePending",
				(purchase: Pick<PurchaseInfo, "sku" | "purchaseToken">) => {
					// alert("IN purchasePending Listener")
					if (purchase.sku && purchase.purchaseToken) {
						pendingPurchases[purchase.sku] = {
							...purchase,
							orderId: "",
							packageName: "",
							acknowledged: false,
							purchaseTime: 0,
							signature: "",
							isAutoRenewing: false,
							purchaseState: 0,
							originalJson: "",
							skus: [purchase.sku],
						}
					}
				}
			)
			listenerHandles.push(purchasePendingListener)
			// Purchase acknowledged
			const purchaseAcknowledgedListener = await Billing.addListener(
				"purchaseAcknowledged",
				async (purchase: Pick<PurchaseInfo, "sku" | "purchaseToken">) => {
					// alert("IN purchaseAcknowledged Listener")

					if (
						userStore.user &&
						purchase.sku &&
						purchase.sku.indexOf("plus") > -1
					) {
						//change subscription right away
						//if sku has plus in it
						userStore.user.subscription_type = "plus"
						//remove banner
						await removeBanner()
					}
					if (purchase.sku && pendingPurchases[purchase.sku]) {
						delete pendingPurchases[purchase.sku]
					}
				}
			)
			listenerHandles.push(purchaseAcknowledgedListener)
			// Acknowledge failed
			const acknowledgeFailedListener = await Billing.addListener(
				"acknowledgeFailed",
				async (error: BillingError) => {
					// alert("IN acknowledgeFailed Listener")
					lastError.value = error
					await removeBanner()
				}
			)
			listenerHandles.push(acknowledgeFailedListener)
		} catch (error) {
			alert("Failed to set up listeners")
			// alert(error.message)
		}
	}

	// Remove event listeners
	const removeListeners = async () => {
		// Remove all listener handles
		for (const handle of listenerHandles) {
			await handle.remove()
		}
		// Also call removeAllListeners for safety
		await Billing.removeAllListeners()
	}

	/**
	 * Load subscription details for a set of SKUs
	 * @param skus - Subscription SKUs to fetch details for
	 */
	const loadSubscriptionDetails = async (skus: string[]) => {
		try {
			const result = await Billing.getSubscriptionDetails({ skus })
			if (result && result.skus) {
				// console.log(JSON.stringify(result, null, 2))
				Object.entries(result.skus).forEach(([sku, details]) => {
					subscriptionDetails[sku] = details
				})
			}
			return result
		} catch (error) {
			lastError.value = error as Error
			throw error
		}
	}

	/**
	 * Get current user subscriptions
	 */
	const loadSubscriptions = async (skus?: string[], restore = false) => {
		try {
			const result = await Billing.getSubscriptions({ skus, restore })
			if (result && result.purchases) {
				if (restore) {
					Object.keys(subscriptions).forEach((sku) => {
						delete subscriptions[sku]
					})
				}
				Object.entries(result.purchases).forEach(([sku, purchase]) => {
					subscriptions[sku] = purchase
				})
				// alert(JSON.stringify(subscriptions, null, 2))
			}
			return result
		} catch (error) {
			lastError.value = error as Error
			throw error
		}
	}

	/**
	 * Purchase a subscription
	 * @param sku - The subscription SKU to purchase
	 */
	const purchaseSubscription = async (
		sku: string,
		token: string,
		accountId: string
	) => {
		try {
			return await Billing.purchaseSubscription({ sku, token, accountId })
		} catch (error) {
			lastError.value = error as Error
			throw error
		}
	}

	/**
	 * Manually acknowledge a purchase
	 * @param purchaseToken - The purchase token to acknowledge
	 */
	const acknowledgePurchase = async (purchaseToken: string) => {
		try {
			return await Billing.acknowledgePurchase({ purchaseToken })
		} catch (error) {
			lastError.value = error as Error
			throw error
		}
	}

	/**
	 * Consume a purchase
	 * @param purchaseToken - The purchase token to consume
	 */
	const consumePurchase = async (purchaseToken: string) => {
		try {
			return await Billing.consumePurchase({ purchaseToken })
		} catch (error) {
			lastError.value = error as Error
			throw error
		}
	}

	/**
	 * Open subscription management
	 * @param sku - Optional SKU for specific subscription management
	 */
	const manageSubscription = async (sku?: string) => {
		try {
			return await Billing.manageSubscription({ sku })
		} catch (error) {
			lastError.value = error as Error
			throw error
		}
	}

	/**
	 * Check if a user has an active subscription for a given SKU
	 * @param sku - The subscription SKU to check
	 * @returns Whether the user has an active subscription
	 */
	const hasActiveSubscription = (sku: string): boolean => {
		return (
			subscriptions[sku] &&
			subscriptions[sku]?.purchaseState === 1 && // PURCHASED state
			subscriptions[sku]?.isAutoRenewing
		)
	}

	// Lifecycle hooks
	onMounted(async () => {
		isConnecting.value = true

		// const dat: GetIsServiceConnectedResult =
		// 	await Billing.getIsServiceConnected()
		// alert(JSON.stringify(dat))
		// isReady.value = dat.value
		setupListeners()
		//poll from device to see if is ready
		await Billing.connect()

		// Initial load of subscriptions
		if (isReady.value && loadSubscriptionsOnReady) {
			loadSubscriptions().catch((error) => {
				console.error("Failed to load subscriptions", error)
			})
		}
	})

	onUnmounted(() => {
		removeListeners()
		Billing.endConnection().catch((error) => {
			console.error("Failed to end billing connection", error)
		})
	})

	return {
		// State
		isReady,
		isConnecting,
		connectionError,
		subscriptions,
		pendingPurchases,
		subscriptionDetails,
		lastError,

		// Methods
		loadSubscriptionDetails,
		loadSubscriptions,
		purchaseSubscription,
		acknowledgePurchase,
		consumePurchase,
		manageSubscription,
		hasActiveSubscription,
	}
}
